import React, { useState, useEffect, useRef } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api, { authService } from '../services/api';
import { usePermisos } from '../hooks/usePermisos';
import { useModoReducido } from '../hooks/useModoReducido';
import { MenuPageIntro, HeaderIOS, SeccionApps, TarjetaApp, InfoCard, IOS } from './shared/MenuLayout';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip as CT, Filler } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, CT, Filler);
const MODULO_ID = 7;

// ── helpers ────────────────────────────────────────────────────────────────
const PillEstado = ({ estado }) => {
  const map = {
    OK:      { bg: 'rgba(52,199,89,.15)',  color: '#1A7F35', label: '✓ OK' },
    PARCIAL: { bg: 'rgba(255,149,0,.15)',  color: '#B36200', label: '⚠ Parcial' },
    ERROR:   { bg: 'rgba(255,59,48,.15)',  color: '#C0392B', label: '✗ Error' },
  };
  const s = map[estado] ?? map['PARCIAL'];
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1.2, py: 0.3,
      borderRadius: '20px', bgcolor: s.bg, color: s.color,
      fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
      {s.label}
    </Box>
  );
};

const fmtFecha = iso => iso
  ? new Date(iso).toLocaleString('es-PE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '—';

// ── Skeleton animado (mientras carga Disponibilidad ME) ─────────────────────
const shimmerSx = {
  '@keyframes pulseShimmer': { '0%,100%': { opacity: 0.35 }, '50%': { opacity: 0.75 } },
  animation: 'pulseShimmer 1.4s ease-in-out infinite',
};

const CardDispMeSkeleton = ({ esAdmin = false }) => (
  <InfoCard color={IOS.purple}>
    <Box sx={{ ...shimmerSx, height: 14, width: '75%', borderRadius: '6px', bgcolor: 'rgba(255,255,255,0.35)', mb: 0.8 }} />
    <Box sx={{ ...shimmerSx, height: 10, width: '45%', borderRadius: '6px', bgcolor: 'rgba(255,255,255,0.25)', mb: 1.8, animationDelay: '0.1s' }} />

    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.5 }}>
      {[0, 1].map(i => (
        <Box key={i} sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: '10px', p: 1.2 }}>
          <Box sx={{ ...shimmerSx, height: 8, width: '60%', borderRadius: '4px', bgcolor: 'rgba(255,255,255,0.3)', mb: 0.8, animationDelay: `${i * 0.15}s` }} />
          <Box sx={{ ...shimmerSx, height: 16, width: '75%', borderRadius: '5px', bgcolor: 'rgba(255,255,255,0.4)', animationDelay: `${i * 0.15 + 0.1}s` }} />
        </Box>
      ))}
    </Box>

    <Box sx={{ height: 145, display: 'flex', alignItems: 'flex-end', gap: '4px', px: 0.5 }}>
      {[38, 55, 42, 68, 50, 72, 60, 80, 65, 90, 78, 95].map((h, i) => (
        <Box key={i} sx={{ ...shimmerSx, flex: 1, height: `${h}%`, borderRadius: '3px 3px 0 0',
          bgcolor: 'rgba(255,255,255,0.3)', animationDelay: `${i * 0.06}s` }} />
      ))}
    </Box>
    <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.72rem', textAlign: 'center', mt: 1.3 }}>
      Calculando disponibilidad{esAdmin ? ' jurisdiccional' : ''}…
    </Typography>
  </InfoCard>
);

// ── Tarjeta 1: Disponibilidad ME ───────────────────────────────────────────
const CardDispMe = ({ data = [], esAdmin = false }) => {
  const ultimo   = data.length ? data[data.length - 1]?.DISP_ME ?? 0 : 0;
  const promedio = data.length
    ? data.reduce((a, d) => a + (d.DISP_ME ?? 0), 0) / data.length
    : 0;

  const labels = data.map(d => {
    const s = String(d.ANNO_MES);
    return `${s.slice(4)}/${s.slice(2, 4)}`;
  });
  const vals = data.map(d => d.DISP_ME ?? 0);

  const cd = {
    labels,
    datasets: [{
      data: vals,
      borderColor: 'rgba(255,255,255,0.9)',
      backgroundColor: 'rgba(255,255,255,0.15)',
      borderWidth: 2,
      fill: true,
      tension: 0.4,
      pointRadius: 4,
      pointHoverRadius: 5,
      pointBackgroundColor: 'rgba(255,255,255,0.9)',
      pointBorderColor: 'rgba(255,255,255,0.3)',
      pointBorderWidth: 1.5,
    }],
  };

  const co = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 600, easing: 'easeInOutQuart' },
    animations: {
      x: { duration: 0 },
      y: { duration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 600, easing: 'easeInOutQuart', delay: ctx => ctx.dataIndex * 80 },
      radius: { duration: 400, easing: 'easeInOutQuart', delay: ctx => ctx.dataIndex * 80 },
    },
    plugins: {
       datalabels: { display: false },
      legend: { display: false },
      tooltip: {
        backgroundColor: '#222B45',
        borderColor: 'rgba(255,255,255,0.3)',
        borderWidth: 1,
        padding: 8,
        borderRadius: 8,
        displayColors: false,
        callbacks: { label: ctx => ` ${ctx.parsed.y.toFixed(2)}%` },
      },
    },
    scales: {
      y: { display: false },
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: 'rgba(255,255,255,0.5)',
          font: { size: 10 },
          maxTicksLimit: 6,
        },
      },
    },
  };

  return (
    <InfoCard color={IOS.purple}>
      <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.9rem', mb: 0.3 }}>
  {esAdmin
    ? 'Medicamentos esenciales · Unidad Ejecutora'
    : 'Disponibilidad de medicamentos esenciales'}
</Typography>
      <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.72rem', mb: 1.5 }}>
        Últimos 12 meses
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.5 }}>
        {[['Último mes', `${ultimo.toFixed(2)}%`], ['Promedio', `${promedio.toFixed(2)}%`]].map(([lbl, val]) => (
          <Box key={lbl} sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: '10px', p: 1.2 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.72rem',
              fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', mb: 0.4 }}>
              {lbl}
            </Typography>
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.1rem' }}>
              {val}
            </Typography>
          </Box>
        ))}
      </Box>

      {data.length > 0 && (
        <Box sx={{ height: 145 }}>
          <Line data={cd} options={co} />
        </Box>
      )}
    </InfoCard>
  );
};

// ── Tarjeta 2: Solicitudes libros controlados ──────────────────────────────
const CardControlados = ({ data = [] }) => {
  const total = data.reduce((a, d) => a + (d.CANTIDAD ?? 0), 0);
  return (
    <InfoCard>
      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: IOS.label, mb: 0.3 }}>
        Solicitudes de libros de controlados
      </Typography>
      <Typography sx={{ fontSize: '0.72rem', color: IOS.label2, mb: 1.5 }}>
        Establecimientos con solicitudes pendientes
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, mb: 1.5 }}>
        {data.length === 0
          ? <Typography sx={{ fontSize: '0.8rem', color: IOS.label2, textAlign: 'center', py: 1 }}>
              Sin solicitudes pendientes
            </Typography>
          : data.map((d, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              bgcolor: 'rgba(0,0,0,.03)', borderRadius: '12px', px: 1.5, py: 1 }}>
              <Box>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: IOS.label }}>
                  {d.ESTABLECIMIENTO}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: IOS.label2 }}>{d.USUARIO}</Typography>
              </Box>
              <Box sx={{ minWidth: 26, height: 26, borderRadius: '20px', bgcolor: '#FF9500',
                display: 'flex', alignItems: 'center', justifyContent: 'center', px: 1 }}>
                <Typography sx={{ color: 'white', fontSize: '0.72rem', fontWeight: 700 }}>
                  {d.CANTIDAD}
                </Typography>
              </Box>
            </Box>
          ))
        }
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        pt: 1, borderTop: `1px solid ${IOS.separator}` }}>
        <Typography sx={{ fontSize: '0.72rem', color: IOS.label2 }}>Total pendientes</Typography>
        <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#FF9500' }}>{total}</Typography>
      </Box>
    </InfoCard>
  );
};

// ── Tarjeta 3: Monitor IPRESS ──────────────────────────────────────────────
const CardMonitorIpress = ({data=[],esAdmin=false,navigate}) => {
 const d=data[0];
 const events=esAdmin?data.reduce((sum,item)=>sum+Number(item.EVENTOS||0),0):Number(d?.EVENTOS||0);
 const ok=esAdmin?data.reduce((sum,item)=>sum+Number(item.OK||0),0):Number(d?.OK||0);
 const errors=esAdmin?data.reduce((sum,item)=>sum+Number(item.ERRORES||0),0):Number(d?.ERRORES||0);
 const pct=events>0?Math.max(0,Math.min(100,ok/events*100)):0;
 return <Box className="farm-activity-card">
   <div className="farm-activity-header"><div><span>Sincronización</span><h2>{esAdmin?'Monitor IPRESS':d?.IPRESS||'Actividad del establecimiento'}</h2><p>{esAdmin?'Actividad por establecimiento':'Actividad del día'}</p></div>{!esAdmin&&d&&<PillEstado estado={d.ESTADO}/>}</div>
   <div className="farm-activity-total"><strong>{d?events.toLocaleString('es-PE'):'—'}</strong><span>eventos registrados</span><span className="farm-activity-pulse" aria-hidden="true"><svg viewBox="0 0 72 30" fill="none"><path d="M1 19h15l7-12 10 20 9-23 8 15h21" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round"/></svg></span></div>
   <div className="farm-activity-results"><div><i className="success"/><span>Correctos</span><strong>{d?ok:'—'}</strong></div><div><i className="error"/><span>Errores</span><strong className={errors?'has-errors':''}>{d?errors:'—'}</strong></div></div>
   <div className="farm-activity-progress" role="img" aria-label={events?pct.toFixed(0)+'% de eventos correctos':'Sin eventos registrados'}><span style={{width:pct+'%'}}/></div>
   {esAdmin?<div className="farm-activity-list">{data.map((item,i)=><div key={i}><div><strong>{item.IPRESS}</strong><small>{item.EVENTOS} eventos · {item.ERRORES} errores</small></div><PillEstado estado={item.ESTADO}/></div>)}</div>:<div className="farm-activity-last"><span>Último evento</span><strong>{fmtFecha(d?.ULTIMO_EVENTO)}</strong></div>}
   {!d&&<p className="farm-activity-empty">Sin actividad disponible.</p>}
   <button type="button" onClick={()=>navigate('/economia/monitor-envios?desde=farmacia')}>Ver monitor de eventos <span aria-hidden="true">→</span></button>
 </Box>;
};

// ── Página principal ───────────────────────────────────────────────────────
const Farmacia = () => {
  const navigate = useNavigate();
  const { tieneOpcion, cargando: cargandoPermisos } = usePermisos();
  const { opcionFarmaciaPermitida } = useModoReducido();
  const puedeVerControlados = !cargandoPermisos && ['narcoticos','psicotropicos'].every(op=>tieneOpcion(op,MODULO_ID)&&opcionFarmaciaPermitida(op));
  const user           = authService.getCurrentUser();
  const nombreCompleto = user?.nombreCompleto || user?.usuario || '';
  const cap = t => t.toLowerCase().split(' ').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
  const nombreFormateado = cap(nombreCompleto);
  const ipress  = user?.ipress || '';
  const esAdmin = [
  'ADMINISTRATIVOS',
  'ALMACENES O CENTROS DE DISTRIBUCION'
].includes(user?.ipressDescripcion);

  const [hovered,     setHovered]     = useState(null);
  const [dispMe,      setDispMe]      = useState([]);
  const [loadDispMe,  setLoadDispMe]  = useState(true);
  const [controlados, setControlados] = useState([]);
  const [monitor,     setMonitor]     = useState([]);

  const handleLogout = () => { authService.logout(); navigate('/login'); };

  useEffect(() => {
    setLoadDispMe(true);
    api.get('/Dashfarmacia/disponibilidad-me-anual')
      .then(r => r.data?.success && setDispMe(r.data.data))
      .catch(() => {})
      .finally(() => setLoadDispMe(false));

    api.get('/Dashfarmacia/monitor-ipress')
      .then(r => r.data?.success && setMonitor(r.data.data))
      .catch(() => {});
  }, []);

  useEffect(()=>{
    let active=true;
    if(!puedeVerControlados){setControlados([]);return undefined;}
    api.get('/Dashfarmacia/solicitudes-controlados').then(r=>{if(active&&r.data?.success)setControlados(r.data.data||[]);}).catch(()=>{if(active)setControlados([]);});
    return ()=>{active=false;};
  },[puedeVerControlados]);

  const totalControlados = controlados.reduce((a, d) => a + (d.CANTIDAD ?? 0), 0);

  const grupoSismed = [
    { id: 'almacen',       nombre: 'Almacén',          iconGris: 'https://img.icons8.com/ios/100/9E9E9E/pallet.png',          iconColor: 'https://img.icons8.com/ios/100/007AFF/pallet.png',          ruta: '/farmacia/almacen' },
    { id: 'productos',     nombre: 'Productos',        iconGris: 'https://img.icons8.com/ios/100/9E9E9E/product.png',         iconColor: 'https://img.icons8.com/ios/100/007AFF/product.png',         ruta: '/farmacia/productos' },
    { id: 'reporte',       nombre: 'Rep. Ventas',      iconGris: 'https://img.icons8.com/ios/100/9E9E9E/combo-chart.png',     iconColor: 'https://img.icons8.com/ios/100/34C759/combo-chart.png',     ruta: '/farmacia/reporte-ventas' },
    { id: 'indicadores',   nombre: 'Indicadores',      iconGris: 'https://img.icons8.com/ios/100/9E9E9E/presentation.png',    iconColor: 'https://img.icons8.com/ios/100/FF9500/presentation.png',    ruta: '/farmacia/indicadores' },
    { id: 'consumos',      nombre: 'Consumos',         iconGris: 'https://img.icons8.com/ios/100/9E9E9E/pill.png',            iconColor: 'https://img.icons8.com/ios/100/5AC8FA/pill.png',            ruta: '/farmacia/consumo' },
    { id: 'consumopac',    nombre: 'Consumo Pac.',     iconGris: 'https://img.icons8.com/ios/100/9E9E9E/exam.png',            iconColor: 'https://img.icons8.com/ios/100/007AFF/exam.png',            ruta: '/farmacia/consumo-paciente' },
    { id: 'requerimiento', nombre: 'Requerimiento',    iconGris: 'https://img.icons8.com/ios/100/9E9E9E/request-service.png', iconColor: 'https://img.icons8.com/ios/100/5E5CE6/request-service.png', ruta: '/farmacia/requerimiento' },
    { id: 'narcoticos',    nombre: 'Narcóticos',       iconGris: 'https://img.icons8.com/ios/100/9E9E9E/syringe.png',         iconColor: 'https://img.icons8.com/ios/100/FF3B30/syringe.png',         ruta: '/farmacia/libro-narcoticos', badge: totalControlados },
    { id: 'psicotropicos', nombre: 'Psicotrópicos',    iconGris: 'https://img.icons8.com/ios/100/9E9E9E/brain.png',           iconColor: 'https://img.icons8.com/ios/100/FF9500/brain.png',           ruta: '/farmacia/libro-psicotropicos' },
    { id: 'conteo',        nombre: 'Rep. Conteo',      iconGris: 'https://img.icons8.com/ios/100/9E9E9E/calculator.png',      iconColor: 'https://img.icons8.com/ios/100/8E8E93/calculator.png',      ruta: '/farmacia/reporte-conteo' },
    { id: 'devoluciones',  nombre: 'Devolución Dosis', iconGris: 'https://img.icons8.com/ios/100/9E9E9E/return-purchase.png', iconColor: 'https://img.icons8.com/ios/100/007AFF/return-purchase.png', ruta: '/farmacia/devoluciones-dosis' },
  ];

  return (
    <Box className="integra-menu" sx={{minHeight:'100vh',bgcolor:IOS.bg}}>
      <HeaderIOS titulo="Farmacia"
        onHome={() => navigate('/dashboard')} onLogout={handleLogout}
        nombreCompleto={nombreFormateado} avatarLetra={nombreCompleto.charAt(0).toUpperCase()} />

      <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, sm: 4 }, pt: 3.5, pb: 6 }}>
        <Grid container spacing={2.5} className="integra-launcher-layout">

          {/* ── Apps ── */}
          <Grid item xs={12} lg={8}>
        <MenuPageIntro title="Farmacia" description="Inventario, medicamentos y actividad de tu establecimiento." />
              <Grid container spacing={1.5}>
                {grupoSismed.map(op => {
                  const bl = (!cargandoPermisos && !tieneOpcion(op.id, MODULO_ID)) || !opcionFarmaciaPermitida(op.id);
                  return (
                    <Grid item xs={4} sm={2} md={2} key={op.id}>
                      <TarjetaApp id={op.id} nombre={op.nombre} iconGris={op.iconGris} iconColor={op.iconColor}
                        ruta={op.ruta} bloqueado={bl} colorHover={IOS.blue} badge={op.badge}
                        hoveredId={hovered} onHover={setHovered} onLeave={() => setHovered(null)} />
                    </Grid>
                  );
                })}
              </Grid>
          </Grid>

          {/* ── Sidebar ── */}
          <Grid item xs={12} lg={4} className="integra-launcher-cards" component="aside" aria-label="Resumen del módulo">

            {/* Tarjeta 1 — Disponibilidad ME (lila, siempre arriba) */}
            {loadDispMe ? <CardDispMeSkeleton esAdmin={esAdmin} /> : <CardDispMe data={dispMe} esAdmin={esAdmin} />}

            {/* Tarjeta 2 — Solicitudes libros controlados */}
            {puedeVerControlados && <CardControlados data={controlados} />}

            {/* Tarjeta 3 — Monitor IPRESS */}
            <CardMonitorIpress data={monitor} esAdmin={esAdmin} navigate={navigate} />

          </Grid>
        </Grid>
      </Box>
      <style>{`
.farm-activity-card{display:flex;flex-direction:column;box-sizing:border-box;padding:22px;background:linear-gradient(145deg,#fff 65%,#f3f7fd);border:1px solid #e0e7f1;border-radius:22px;box-shadow:0 8px 24px #294b7510;margin-bottom:16px;min-width:0}
.farm-activity-header{display:flex;align-items:flex-start;justify-content:space-between;gap:12px}.farm-activity-header>div{min-width:0}.farm-activity-header span{font-size:10px;color:#8495aa}.farm-activity-header h2{font-size:15px;font-weight:650;color:#344a65;margin:6px 0 0;line-height:1.4;overflow-wrap:anywhere}.farm-activity-header p{font-size:11px;color:#8a98aa;margin:4px 0 0}
.farm-activity-total{display:flex;align-items:baseline;gap:9px;margin:22px 0 18px}.farm-activity-total>strong{font-size:34px;font-weight:650;letter-spacing:-1px;color:#345779}.farm-activity-total>span{font-size:11px;color:#8797aa}.farm-activity-total .farm-activity-pulse{margin-left:auto;width:65px;color:#87b4dc;align-self:center;flex-shrink:0}.farm-activity-pulse svg{width:100%;display:block}
.farm-activity-results{display:grid;grid-template-columns:1fr 1fr;gap:16px}.farm-activity-results>div{display:flex;align-items:center;gap:6px;font-size:11px;color:#7b8b9f}.farm-activity-results strong{margin-left:auto;font-size:15px;color:#437d66}.farm-activity-results strong.has-errors{color:#ca6678}.farm-activity-results i{width:6px;height:6px;border-radius:50%;flex-shrink:0}.farm-activity-results i.success{background:#69b18e}.farm-activity-results i.error{background:#d68b98}
.farm-activity-progress{height:5px;background:#eaf0f5;border-radius:5px;overflow:hidden;margin-top:13px}.farm-activity-progress>span{height:100%;display:block;background:#74b89a;border-radius:5px}
.farm-activity-last{display:flex;justify-content:space-between;flex-wrap:wrap;gap:8px;font-size:10px;color:#8a99ab;margin:17px 0}.farm-activity-last strong{font-weight:500;color:#61758e}
.farm-activity-card>button{margin-top:auto;display:flex;justify-content:space-between;gap:10px;align-items:center;min-height:40px;padding:10px 13px;border:1px solid #dce7f5;border-radius:10px;background:#edf4fd;color:#527eaf;font:inherit;font-size:12px;font-weight:600;cursor:pointer}.farm-activity-card>button:hover{background:#e2eefc}.farm-activity-card>button:focus-visible{outline:2px solid #548ee6;outline-offset:3px}
.farm-activity-list{max-height:180px;overflow:auto;margin:16px 0}.farm-activity-list>div{display:flex;gap:10px;justify-content:space-between;align-items:center;padding:10px 0;border-bottom:1px solid #edf1f6}.farm-activity-list strong{display:block;font-size:11px;color:#586c85}.farm-activity-list small{display:block;font-size:10px;color:#8b99ac;margin-top:4px}.farm-activity-empty{font-size:12px;color:#8796a9}
@media(max-width:1199px){.integra-menu .integra-launcher-cards>.farm-activity-card{display:flex;flex-direction:column;justify-content:space-between;gap:0}.farm-activity-total{margin:18px 0}.farm-activity-last{margin:14px 0}}
`}</style>
    </Box>
  );
};

export default Farmacia;